import { useEffect } from "react";

import { Button, Typography } from "@mui/material";
import classNames from "classnames";
import { useForm, useWatch } from "react-hook-form";

import { useAccount } from "../../../../../hooks/useAccount";
import { rules } from "../../../../../utils";
import { CommonInput } from "../../../../common";
import { Subscriptions } from "./Subscription";

import "./account.scss";

export type TAccountFieldValues = {
	firstName: string;
	lastName: string;
	email: string;
	phoneNumber: string;
};

export function Account() {
	const { account, saveUserInfo } = useAccount();

	const defaultValues: TAccountFieldValues = {
		firstName: account?.firstName ?? "",
		lastName: account?.lastName ?? "",
		email: account?.email ?? "",
		phoneNumber: account?.phoneNumber ?? "",
	};

	const { handleSubmit, control, reset } = useForm<TAccountFieldValues>({
		defaultValues,
		mode: "all",
	});
	const { firstName, lastName, phoneNumber } = useWatch({ control });

	useEffect(() => {
		if (account) {
			reset({
				firstName: account.firstName,
				lastName: account.lastName,
				email: account.email,
				phoneNumber: account.phoneNumber,
			});
		}
	}, [account, reset]);

	const onClickSave = async (data: TAccountFieldValues) => {
		saveUserInfo(data);
	};

	const onClickCancel = () => {
		if (account) {
			reset({
				firstName: account.firstName,
				lastName: account.lastName,
				email: account.email,
				phoneNumber: account.phoneNumber,
			});
		}
	};

	const isDisabled =
		firstName === (account?.firstName ?? "") &&
		lastName === (account?.lastName ?? "") &&
		phoneNumber === (account?.phoneNumber ?? "");
	return (
		<div className="right-column-content-container">
			<div className="account-container">
				<Typography className="small-title-text bold">
					Account Information
				</Typography>
				<div className="item-separator-tiny">
					<form onSubmit={handleSubmit(onClickSave)}>
						<div className="form-inputs">
							<CommonInput
								control={control}
								name="email"
								type="email"
								placeholder="Your email*"
								disabled
								rules={{
									required: {
										value: true,
										message: "Email is required.",
									},
									pattern: rules.email.pattern,
								}}
							/>
							<CommonInput
								control={control}
								name="phoneNumber"
								type="tel"
								placeholder="Your phone number"
								rules={{
									pattern: rules.phoneNumber.pattern,
								}}
							/>
							<CommonInput
								control={control}
								name="firstName"
								placeholder="First name*"
								rules={{
									required: {
										value: true,
										message: "First name is required.",
									},
									minLength: rules.firstName.min,
									maxLength: rules.firstName.max,
								}}
							/>
							<CommonInput
								control={control}
								name="lastName"
								placeholder="Last name*"
								rules={{
									required: {
										value: true,
										message: "Last name is required.",
									},
									minLength: rules.lastName.min,
									maxLength: rules.lastName.max,
								}}
							/>
						</div>
						<div className="save-account-button-container item-separator-small">
							<Button
								onClick={onClickCancel}
								className="normal-button disabled-button no-transform">
								Cancel
							</Button>
							<Button
								variant="contained"
								className={classNames("normal-button no-transform", {
									"primary-button": !isDisabled,
									"disabled-button": isDisabled,
								})}
								type="submit">
								Save
							</Button>
						</div>
					</form>
				</div>
			</div>
			<div className="section-separator" />
			<Subscriptions />
		</div>
	);
}
